import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './ChangeRequestInput.less';
import { Form, Button, FormProps, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import {
  clarifyChangeRequest,
  planChangeRequest,
  getChangeRequest,
  editChangeRequestItem,
} from '../../../app/ActionTypes/changeRequestActionTypes';
import { unwrapResult } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import {
  useAgentSelector,
  useAgentDispatch,
} from '../../../hooks/useAgentStore';
import {
  changeRequestResultPolling,
  implementAllActionItems,
  isOwnChangeRequest,
} from '../../../services/changeRequestService';
import {
  setChangeRequestId,
  setRevokeAPICalls,
} from '../../../app/Slices/changeRequestStateSlice';
import { useParams } from 'react-router-dom';
import { ChangeRequestSource } from '../../../enums/changeRequest';
import { useReactToPrint } from 'react-to-print';
import ChangeRequestExportView from '../ChangeRequestExportView/ChangeRequestExportView';
import MarkdownEditor from '../../MarkdownEditor/MarkdownEditor';
import { MarkdownText } from '../../../interfaces/markdown';
import { Notify } from '../../../services/notificationService';
import { EditOutlined } from '@ant-design/icons';
import * as subscriptionService from '../../../services/subscription';
import { getAppLoaderController } from '../../../hooks/loadingContext';
import ChangeRequestExportDropdown from '../ChangeRequestExportDropdown/ChangeRequestExportDropdown';
import { NOT_OWN_CHANGE_REQUEST } from '../../../constants/texts';
import { fetchChangeRequests } from '../../../app/ActionTypes/changeRequestsActions';
import { selectSubscriptionStatus } from '../../../app/Slices/subscriptionSlice';

type FieldType = {
  textInput: MarkdownText;
};

const notifyError = (error: string | { error: string } | Error | unknown) => {
  let errorStr;
  if (typeof error === 'string') {
    errorStr = error;
  } else if (error instanceof Error) {
    errorStr = error.message;
  } else {
    errorStr = (error as { error: string })?.error || String(error);
  }
  Notify.error(errorStr, 0);
};

const STEP_ANALYSIS = 0;
const STEP_ACTIONS = 1;
const STEP_SUMMARY = 2;

const ChangeRequestInput: React.FC = () => {
  // Get ID from URL if present
  const { changeRequestRouteId } = useParams<{
    changeRequestRouteId: string;
  }>();

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
  });

  const [form] = Form.useForm();
  const agentDispatch = useAgentDispatch();

  const org = useSelector((state: RootState) => state.organization.org);
  const status = useSelector(selectSubscriptionStatus);
  const isUserReadonly = subscriptionService.isUserReadonly(status);

  // Get the current state from Redux
  const { data, loading, isImplementing } = useAgentSelector(
    (state) => state.changeRequest
  );
  const cr = useMemo(() => {
    return data?.result;
  }, [data?.result]);

  const { currentStep } = useAgentSelector((state) => state.changeRequestSteps);
  const changeRequestId = useAgentSelector(
    (state) => state.changeRequestState.changeRequestId
  );

  const [isEditMode, setEditMode] = useState(false);
  const [isModified, setIsModified] = useState<boolean>(false);
  let updatedSummary = '';

  const stickyElementRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setSticky] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Helper variables to check the state of items
  const hasChecklist = Boolean(cr?.checklist?.items?.length);
  const hasExecutionItems = Boolean(cr?.execution?.items?.length);

  const isAnalysisStep = currentStep === STEP_ANALYSIS;
  const isActionsStep = currentStep === STEP_ACTIONS;
  const isSummaryStep = currentStep === STEP_SUMMARY;

  const isCrReadOnly = !cr || !isOwnChangeRequest(org, cr);
  const isReadonly = isCrReadOnly || isUserReadonly;
  const readonlyMsg = isCrReadOnly
    ? NOT_OWN_CHANGE_REQUEST
    : subscriptionService.getReadonlyElementTitle();

  const onAnalyzeRequest = async (
    requestText: string,
    id: string,
    name: string
  ) => {
    try {
      agentDispatch(setRevokeAPICalls(false));
      // Step 2: Clarify Change Request with predefined strategy
      const clarifyResponse = await agentDispatch(
        clarifyChangeRequest({
          id,
          name,
          summary: requestText,
          source: ChangeRequestSource.ANALYSIS_CHECKLIST,
        })
      );

      handleOperationResponse(clarifyResponse);
      await agentDispatch(fetchChangeRequests());
    } catch (error: unknown) {
      notifyError(error);
    }
  };

  const onGenerateActionItems = async (requestText: string) => {
    try {
      if (cr) {
        const { id, name, checklist } = cr;
        agentDispatch(setRevokeAPICalls(false));
        const response = await agentDispatch(
          planChangeRequest({
            id,
            name,
            summary: requestText,
            checklist,
            source: ChangeRequestSource.ACTIONS_EXECUTION,
          })
        );
        handleOperationResponse(response);
        await agentDispatch(fetchChangeRequests());
      }
    } catch (error: unknown) {
      notifyError(error);
    }
  };

  const handleOperationResponse = async (
    response: ReturnType<typeof unwrapResult>
  ) => {
    try {
      const {
        data: { id: operationId },
      } = unwrapResult(response); // Ensure you unwrap the result for any further actions

      if (!operationId) {
        Notify.warning('Failed to create Change Request Actions');
        return;
      }

      await changeRequestResultPolling(
        agentDispatch,
        operationId,
        isAnalysisStep
          ? ChangeRequestSource.ANALYSIS_CHECKLIST
          : ChangeRequestSource.ACTIONS_EXECUTION
      );
    } catch (error: unknown) {
      notifyError((error as Error).message || 'Unable to perform action');
    }
  };

  const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
    const requestText = updatedSummary || cr?.summary || '';
    if (requestText.trim() === '') {
      Notify.warning('Please enter a valid Change Request');
      return;
    }

    const id = cr?.id;
    const name = cr?.name || '';

    if (id && isAnalysisStep) {
      onAnalyzeRequest(requestText, id, name);
    } else if (isActionsStep) {
      onGenerateActionItems(requestText);
    }
  };

  const onImplementAll = async () => {
    if (!cr?.execution?.items?.length) {
      Notify.warning('No action items available to implement!');
      return;
    }

    implementAllActionItems(cr, agentDispatch);
  };

  /**
   * Handles the cancel action for the change request process.
   */
  const revokeAPICall = () => {
    agentDispatch(setRevokeAPICalls(true));
    if (changeRequestRouteId) {
      agentDispatch(setChangeRequestId(changeRequestRouteId));
      agentDispatch(getChangeRequest(changeRequestRouteId));
    }
  };

  const handlePrint = () => {
    reactToPrintFn();
  };

  const ElementToPrint = () => {
    return (
      <div
        style={{ display: 'none', backgroundColor: 'var(--neutral-color-1)' }}
      >
        <ChangeRequestExportView ref={contentRef} />
      </div>
    );
  };

  const editSummary = () => {
    setSummaryState(false, true, true);
    setIsModified(false);
  };

  const updateSummary = async () => {
    const markdownText = form.getFieldValue('textInput')?.markdownText;
    if (markdownText.trim() === '') {
      Notify.warning('Please enter a valid Change Request');
      return;
    }
    const { setAppLoading } = getAppLoaderController();
    if (!changeRequestId) {
      Notify.error('No change request selected');
      return;
    }

    updatedSummary = markdownText;
    setSummaryState(true, false, false, markdownText);
    try {
      setAppLoading(true);
      const response = await agentDispatch(
        editChangeRequestItem({
          path: `/summary`,
          updatedItem: markdownText,
          crId: changeRequestId,
          source: ChangeRequestSource.EDIT_SUMMARY,
        })
      );
      await agentDispatch(fetchChangeRequests());
      const payload = response.payload as { error?: boolean };
      if (payload.error) {
        Notify.error('Error editing change request details');
      }
      Notify.success('Change request details updated successfully!');
    } catch (error) {
      Notify.error('Error editing change request details');
    } finally {
      setAppLoading(false);
    }
  };

  const onCancel = () => {
    setSummaryState(true, false, false);
  };

  const setSummaryState = (
    previewMode: boolean,
    showTabs: boolean,
    editMode: boolean,
    markdownText?: string
  ) => {
    form.setFieldsValue({
      textInput: {
        markdownText: markdownText ?? (cr?.summary || ''),
        isPreviewMode: previewMode,
        showTabs: showTabs,
      },
    });
    setEditMode(editMode);
    updatedSummary = cr?.summary || '';
    const changeRequestElement = document.getElementById('change-request');
    if (changeRequestElement) {
      changeRequestElement.style.overflowY = editMode ? 'hidden' : 'auto';
    }
  };

  const handleScroll = useCallback(() => {
    const scrollContainer = document.getElementById('change-request');
    if (!scrollContainer || !stickyElementRef.current) return;

    const rect = stickyElementRef.current.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const isElementSticky = rect.top < containerRect.top;

    setSticky(isElementSticky);

    // Cache sticky elements outside of loop
    const stickyElements = document.querySelectorAll('.cr-sticky-element');

    stickyElements.forEach((el) => {
      el.classList.toggle('sticky', isElementSticky);
    });
  }, []);

  const handleEscapeKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'escape' && isEditMode) {
        onCancel();
      }
    },
    [isEditMode]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleEscapeKeyPress);

    return () => {
      window.removeEventListener('keydown', handleEscapeKeyPress);
    };
  }, [handleEscapeKeyPress]);

  useEffect(() => {
    const scrollContainer = document.getElementById('change-request');
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (cr?.summary) {
      setSummaryState(true, false, false);
    } else {
      form.resetFields();
      updatedSummary = '';
    }
  }, [data]);

  return (
    <>
      {isExporting && <div className="linear-loading"></div>}

      <div id="change-request-input">
        <ElementToPrint />
        <div className="header-bar">
          <div className="title">
            {isSummaryStep ? (
              <h1 className="bold-text">Summary of Change Request</h1>
            ) : (
              <h1>Change Request Details</h1>
            )}
          </div>
          <div className="button-container">
            {isSummaryStep && <Button onClick={handlePrint}>Print</Button>}
            <ChangeRequestExportDropdown
              setIsLoading={setIsExporting}
              disabled={!cr || isImplementing || loading}
              pdf={isSummaryStep}
              docx={isSummaryStep}
              xlsx={isSummaryStep}
              json
            />
          </div>
        </div>

        <div className="change-request-input-container">
          {isAnalysisStep && !isEditMode ? (
            <div className="edit-summary-button-container">
              <Tooltip
                title={readonlyMsg}
                placement="right"
                destroyOnHidden={false}
              >
                <Button
                  className="edit-summary-button"
                  onClick={editSummary}
                  icon={<EditOutlined />}
                  title="Edit Request Details"
                  disabled={isReadonly || loading}
                ></Button>
              </Tooltip>
            </div>
          ) : null}

          <Form
            form={form}
            onFinish={onSubmit}
            layout="vertical"
            onValuesChange={(changedValues, allValues) => {
              setIsModified(allValues.textInput.markdownText !== cr?.summary);
            }}
            data-hs-do-not-collect="true"
          >
            {isEditMode && <div className="custom-modal-overlay"></div>}
            <div
              className={`${isEditMode ? 'custom-modal edit-summary-modal' : ''}`}
            >
              <div
                onDoubleClick={() => {
                  if (
                    isAnalysisStep &&
                    !isEditMode &&
                    !isReadonly &&
                    !loading
                  ) {
                    const scrollContainer =
                      document.getElementById('change-request');
                    if (scrollContainer) {
                      scrollContainer.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                      });
                    }
                    setTimeout(() => {
                      editSummary();
                    }, 100);
                  }
                }}
              >
                <Form.Item
                  name="textInput"
                  valuePropName="value"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your request details!',
                    },
                  ]}
                >
                  <MarkdownEditor isReadonly={isReadonly} />
                </Form.Item>
              </div>
              {isEditMode && (
                <div className="button-wrapper change-request-input-button">
                  <Button onClick={onCancel}>Cancel</Button>
                  <Tooltip title={!isModified ? 'No updates to save' : ''}>
                    <Button
                      type="primary"
                      onClick={updateSummary}
                      disabled={!isModified}
                    >
                      Save Changes
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
            <Form.Item>
              <div ref={stickyElementRef}></div>
              <div
                className={`button-wrapper sticky-element ${
                  isSticky ? 'sticky-base sticky' : ''
                }`}
              >
                {isAnalysisStep && (
                  <Tooltip
                    title={readonlyMsg}
                    placement="top"
                    destroyOnHidden={false}
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={loading || isReadonly}
                    >
                      {hasChecklist ? 'Reanalyze Request' : 'Analyze Request'}
                    </Button>
                  </Tooltip>
                )}

                {isActionsStep && (
                  <>
                    <Tooltip title={readonlyMsg} destroyOnHidden={false}>
                      <Button
                        type={hasExecutionItems ? 'default' : 'primary'}
                        htmlType="submit"
                        disabled={loading || !org?.active || isReadonly}
                      >
                        {hasExecutionItems
                          ? 'Regenerate Action Items'
                          : 'Generate Action Items'}
                      </Button>
                    </Tooltip>

                    {hasExecutionItems && (
                      <Tooltip
                        title={
                          isReadonly
                            ? readonlyMsg
                            : 'Implement all eligible automated tasks'
                        }
                        destroyOnHidden={false}
                      >
                        <Button
                          type="primary"
                          disabled={loading || !org?.active || isReadonly}
                          onClick={org?.active ? onImplementAll : undefined}
                        >
                          Implement All
                        </Button>
                      </Tooltip>
                    )}
                  </>
                )}
                {(isImplementing || loading) && (
                  <Button onClick={revokeAPICall}>Cancel</Button>
                )}
              </div>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};

export default ChangeRequestInput;
